/**
 * TopBarLayout - A simplified layout with only the top bar (AppTopBar)
 *
 * This layout provides a minimal page structure with just the application
 * header, without the sidebar navigation. Useful for preview pages,
 * viewer pages, and other full-screen content experiences.
 *
 * @author Generated for document preview feature
 */

import React from 'react';

import ErrorBoundary from 'signer-app/parts/error-boundary';
import HsStyleProvider from 'hellosign/common/style/hs-style-provider';
import { useHSUser } from 'hellospa/common/hs-user';
import { getCSRFToken } from 'js/sign-components/common/hs-fetch';
import SkipToContentLink from 'hellospa/components/skip-to-content-link';
import { locale } from 'hellospa/common/hs-intl-provider';
import styles from './top-bar-layout.module.scss';

import { useDefaultLayout } from './context';
import GlobalHeaderWithNotifier from 'hellosign/components/app/header';

interface TopBarLayoutProps {
  withSearch?: boolean;
  onSearch?: (query: string) => void;
  children?: React.ReactNode;
}

export function TopBarLayout(props: TopBarLayoutProps) {
  const context = useDefaultLayout();

  const { app, csrfToken, signature, siteCode } = context;

  const user = useHSUser();
  const signatureModalProps = {
    signatureType: signature.type,
    settings: user.settings,
    csrfToken,
  };

  React.useEffect(
    () => {
      if (app) {
        if (csrfToken) {
          (app as any).csrfToken = csrfToken;
        } else {
          (app as any).csrfToken = getCSRFToken();
        }
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [],
  ); // Only run on mount.

  return (
    <HsStyleProvider>
      <div className={styles.topBarLayout}>
        <SkipToContentLink />
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <GlobalHeaderWithNotifier
              withSearch={props.withSearch}
              onSearch={props.onSearch}
              locale={locale}
              responsive
              app={app}
              userId={user.id}
              referUrl={user.referUrl}
              siteCode={siteCode}
              isFree={user.isFree}
              emailAddress={user.emailAddress}
              name={user.name}
              numDocsLeft={user.numDocsLeft}
              numFaxPagesLeft={user.numFaxPagesLeft}
              numApiDocsLeft={user.numApiDocsLeft}
              numTemplatesLeft={user.numTemplatesLeft}
              primarySignatureGuid={user.primarySignatureGuid}
              showApiCredits={user.showApiCredits}
              signatureModalProps={signatureModalProps}
              canAccessAdminConsole={user.canAccessAdminConsole}
              isFromSelfServeApi={user.isFromSelfServeApi}
              isDropboxManaged={user.isDropboxManaged}
              showDelinquencyBanner={user.showDelinquencyBanner}
              nextBillingDate={user.nextBillingDate}
              endDate={user.endDate}
              canManageBilling={user.canManageBilling}
              prevPlanName={user.prevPlanName}
              hasAlternativeBilling={user.hasAlternativeBilling}
              hasFrozenSubscription={user.hasFrozenSubscription}
              freeTrialEndDate={user.freeTrialEndDate}
              ccExpiredBeforeRenewal={user.ccExpiredBeforeRenewal}
              ccExpiringBeforeRenewal={user.ccExpiringBeforeRenewal}
              showExpiredCCNotification={user.showExpiredCCNotification}
            />
          </div>
          <div className={styles.content}>
            <main id="site-wrapper" className={styles.main} tabIndex={-1}>
              <ErrorBoundary>{props.children}</ErrorBoundary>
            </main>
          </div>
        </div>
      </div>
    </HsStyleProvider>
  );
}
